'use client'

import React from 'react'
import { Heart, Pin, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ambiguousNames, isVerified } from '../lib/amm'
import { usdCompact } from '../lib/format'
import { useAlkanes } from '../lib/store'
import type { AssetRow as Asset } from '../lib/useAssets'
import { nameFlags } from './asset-badges'
import { BadgedIcon, Change } from './asset-row'
import { formatUsdPrice } from '../lib/format'
import useTwetchFeed from '../lib/use-twetch-feed'
import { TwetchPostList } from './social-feed'

type RailTab = 'alerts' | 'tokens' | 'leaderboard' | 'feed'
type RailLens = 'verified' | 'trending' | 'held' | 'gainers'

const RANK_MIN_LIQUIDITY_USD = 1_000

/// A ghost bar — the width a value will occupy, unmistakably not a value.
function Bar({ className }: { className?: string }) {
	return <span className={cn('block h-3 rounded bg-secondary', className)} aria-hidden="true" />
}

function GhostAvatar({ size = 32 }: { size?: number }) {
	return (
		<span
			aria-hidden="true"
			style={{ width: size, height: size }}
			className="block shrink-0 rounded-full bg-secondary"
		/>
	)
}

/// One compact rail token row, fomo web's exact columns: symbol over price on
/// the left, market cap over the 24h triangle on the right. The id keeps a
/// quiet slot — five live tokens answer to "pi", and only the id tells them
/// apart.
function RailTokenRow({
	asset,
	verified,
	selected,
	onSelect
}: {
	asset: Asset
	verified: boolean
	selected: boolean
	onSelect: (a: Asset) => void
}) {
	return (
		<button
			type="button"
			data-testid={`alkanes-rail-token-${asset.id}`}
			onClick={() => onSelect(asset)}
			aria-current={selected ? 'true' : undefined}
			className={cn(
				'flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left transition-colors',
				'hover:bg-secondary/50 active:bg-secondary',
				selected && 'bg-secondary/70'
			)}
		>
			<BadgedIcon
				token={{ id: asset.id, name: asset.name, icon: asset.icon }}
				verified={verified}
				size={34}
			/>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-bold leading-tight">
					{asset.name || asset.id}
				</span>
				<span className="block truncate text-xs tabular-nums text-muted-foreground">
					<span data-testid="alkanes-rail-token-price">
						{asset.price_usd !== null ? formatUsdPrice(asset.price_usd) : '—'}
					</span>
					<span className="ml-1.5 text-[10px]">{asset.id}</span>
				</span>
			</span>
			<span className="flex shrink-0 flex-col items-end gap-0.5">
				<span className="text-xs font-semibold tabular-nums">
					{asset.market_cap_usd !== null && asset.market_cap_usd !== undefined
						? `${usdCompact(asset.market_cap_usd)} MC`
						: `${usdCompact(asset.liquidity_usd)} liq`}
				</span>
				<Change v={asset.change_24h_pct} className="text-xs font-medium" />
			</span>
		</button>
	)
}

/// A compact feed/alert event row as an explicitly empty frame: the chips are
/// real (that is the anatomy), the names and figures are ghost bars — nothing
/// here may look like a post someone could trade on.
function GhostEventRow({ kind }: { kind: 'buy' | 'sell' | 'thesis' }) {
	const chip =
		kind === 'buy' ? (
			<span className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-bold text-success-ink">
				Buy
			</span>
		) : kind === 'sell' ? (
			<span className="rounded bg-destructive/20 px-1.5 py-0.5 text-[10px] font-bold text-destructive-ink">
				Sell
			</span>
		) : (
			<span className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-bold text-primary">
				Thesis
			</span>
		)
	return (
		<div className="border-b border-border/40 p-3">
			<div className="flex items-center gap-2">
				<GhostAvatar size={28} />
				<Bar className="w-20" />
				{chip}
				<span className="flex-1" />
				<Bar className="w-6" />
			</div>
			<div className="mt-2 flex items-center gap-2 pl-9">
				<GhostAvatar size={16} />
				<Bar className="w-12" />
				<Bar className="w-16" />
			</div>
			{kind === 'thesis' && (
				<div className="mt-2 space-y-1.5 pl-9">
					<Bar className="w-full max-w-[200px]" />
					<Bar className="w-3/4 max-w-[160px]" />
				</div>
			)}
			<div className="mt-2 flex items-center gap-1.5 pl-9 text-muted-foreground/50">
				<Heart className="size-3.5" aria-hidden="true" />
				<Bar className="w-4" />
			</div>
		</div>
	)
}

/// The explainer, styled as the rail's pinned post — the slot fomo uses for
/// its official recap. Ours states plainly what this surface will become.
function PinnedExplainer({ text }: { text: string }) {
	return (
		<div className="border-b border-border/40 p-3">
			<div className="flex items-center gap-2">
				<img src="/logo-icon.svg" alt="" width={24} height={24} className="size-6" />
				<span className="text-sm font-bold">Ordinals Wallet</span>
				<span className="flex-1" />
				<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
					<Pin className="size-3.5" aria-hidden="true" /> Pinned
				</span>
			</div>
			<p className="mt-2 rounded-xl bg-secondary/40 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
				{text}
			</p>
		</div>
	)
}

/// The rail's Feed tab, live: the same Twetch feed the Social tab carries,
/// read-only in the rail's width. Joining and composing stay on the Social
/// tab — the rail is a monitor, not a door.
function RailFeed() {
	const feed = useTwetchFeed(true)
	return (
		<div className="min-h-0 flex-1 overflow-y-auto pb-2" data-testid="alkanes-rail-feed">
			<TwetchPostList feed={feed} />
		</div>
	)
}

function GhostRankRow({ n }: { n: number }) {
	const medal =
		n === 1
			? 'bg-warning/25 text-warning-ink'
			: n === 2
			? 'bg-secondary text-muted-foreground'
			: n === 3
			? 'bg-destructive/20 text-destructive-ink'
			: ''
	return (
		<div className="flex items-center gap-2.5 px-3 py-2.5">
			{n <= 3 ? (
				<span
					className={cn(
						'grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-bold tabular-nums',
						medal
					)}
				>
					{n}
				</span>
			) : (
				<span className="w-5 shrink-0 text-center text-xs tabular-nums text-muted-foreground">
					{n}.
				</span>
			)}
			<GhostAvatar size={32} />
			<div className="min-w-0 flex-1 space-y-1.5">
				<Bar className="w-20" />
				<Bar className="w-14" />
			</div>
			<Bar className="w-14" />
		</div>
	)
}

/// The workspace's left rail. Alerts and Leaderboard stay implemented below but
/// are deliberately absent until their backing services are ready to ship.
export default function WorkspaceRail() {
	const assets = useAlkanes((s) => s.assets)
	const assetId = useAlkanes((s) => s.assetId)
	const verifiedIds = useAlkanes((s) => s.verifiedIds)
	const navigate = useAlkanes((s) => s.navigate)

	const [tab, setTab] = React.useState<RailTab>('tokens')
	const [lens, setLens] = React.useState<RailLens>('trending')
	const [query, setQuery] = React.useState('')

	const ambiguous = React.useMemo(
		() => ambiguousNames(assets.map((a) => ({ id: a.id, name: a.name }))),
		[assets]
	)
	const additionalVerified = React.useMemo(
		() => assets.filter((candidate) => verifiedIds.includes(candidate.id)),
		[assets, verifiedIds]
	)

	// "Most held" only exists when the registry actually carries holder
	// counts — the pill vanishes rather than sorting on a fabricated zero.
	const hasHolders = React.useMemo(
		() => assets.some((a) => Number.isFinite((a as { holders?: number }).holders)),
		[assets]
	)

	const q = query.trim().toLowerCase()
	const rows = React.useMemo(() => {
		let list = [...assets]
		if (q) {
			return list
				.filter((a) => (a.name || '').toLowerCase().includes(q) || a.id.toLowerCase().includes(q))
				.sort((a, b) => {
					const score = (x: Asset) =>
						(isVerified(x.id, verifiedIds) ? 2 : 0) +
						((x.name || '').toLowerCase().startsWith(q) ? 1 : 0)
					const d = score(b) - score(a)
					return d !== 0 ? d : b.liquidity_usd - a.liquidity_usd
				})
		}
		switch (lens) {
			case 'verified':
				return list
					.filter((a) => isVerified(a.id, verifiedIds))
					.sort((a, b) => b.liquidity_usd - a.liquidity_usd)
			case 'held':
				return list
					.filter((a) => Number.isFinite((a as { holders?: number }).holders))
					.sort(
						(a, b) =>
							((b as { holders?: number }).holders ?? 0) -
							((a as { holders?: number }).holders ?? 0)
					)
			case 'gainers':
				return list
					.filter((a) => a.change_24h_pct !== null && a.liquidity_usd >= RANK_MIN_LIQUIDITY_USD)
					.sort((a, b) => b.change_24h_pct! - a.change_24h_pct!)
			case 'trending':
				return list.sort((a, b) => {
					const av = a.volume_24h_usd
					const bv = b.volume_24h_usd
					if (av === null && bv === null) {
						return b.liquidity_usd - a.liquidity_usd
					}
					if (av === null) {
						return 1
					}
					if (bv === null) {
						return -1
					}
					return bv - av
				})
		}
	}, [assets, lens, verifiedIds, q])

	const onSelect = React.useCallback((a: Asset) => navigate('home', a.id), [navigate])

	const [lbWindow, setLbWindow] = React.useState<'24H' | '7D' | '30D' | 'ALL'>('24H')

	return (
		<aside
			aria-label="Workspace rail"
			data-testid="alkanes-rail"
			className="flex h-full min-h-0 flex-col rounded-2xl bg-card/60"
		>
			<div
				role="tablist"
				aria-label="Rail sections"
				className="flex gap-1 border-b border-border/60 px-2 pt-1"
			>
				{(
					[
						['tokens', 'Tokens'],
						['feed', 'Feed']
					] as const
				).map(([id, label]) => {
					const on = tab === id
					return (
						<button
							key={id}
							type="button"
							role="tab"
							aria-selected={on}
							data-testid={`alkanes-rail-tab-${id}`}
							onClick={() => setTab(id)}
							className={cn(
								'relative p-2.5 text-sm font-semibold transition-colors',
								on ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
							)}
						>
							{label}
							{on && (
								<span
									aria-hidden="true"
									className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary"
								/>
							)}
						</button>
					)
				})}
			</div>

			{tab === 'tokens' && (
				<React.Fragment>
					<div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
						{(
							[
								['verified', 'Verified'],
								['trending', 'Trending'],
								...(hasHolders ? ([['held', 'Most held']] as const) : []),
								['gainers', 'Gainers']
							] as const
						).map(([id, label]) => {
							const on = !q && lens === id
							return (
								<button
									key={id}
									type="button"
									aria-pressed={on}
									data-testid={`alkanes-rail-lens-${id}`}
									onClick={() => {
										setQuery('')
										setLens(id)
									}}
									className={cn(
										'rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
										on
											? 'bg-foreground text-background'
											: 'bg-secondary text-muted-foreground hover:text-foreground'
									)}
								>
									{label}
								</button>
							)
						})}
					</div>
					<div className="mx-3 mb-1 flex items-center gap-2 rounded-lg bg-secondary/50 px-2.5">
						<Search className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
						<input
							type="search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							aria-label="Filter tokens"
							placeholder="Filter tokens"
							className="h-8 w-full min-w-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
						/>
					</div>
					<div
						className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2"
						data-testid="alkanes-rail-tokens"
					>
						{rows.length === 0 ? (
							<p className="px-3 py-8 text-center text-xs text-muted-foreground">
								{q ? 'Nothing matches.' : 'No assets clear this filter right now.'}
							</p>
						) : (
							rows.map((a) => (
								<RailTokenRow
									key={a.id}
									asset={a}
									verified={nameFlags(a, ambiguous, additionalVerified).verified}
									selected={a.id === assetId}
									onSelect={onSelect}
								/>
							))
						)}
					</div>
				</React.Fragment>
			)}

			{tab === 'leaderboard' && (
				<div className="min-h-0 flex-1 overflow-y-auto pb-2">
					<div className="flex items-center gap-1.5 px-3 py-2">
						{(['24H', '7D', '30D', 'ALL'] as const).map((w) => (
							<button
								key={w}
								type="button"
								aria-pressed={lbWindow === w}
								onClick={() => setLbWindow(w)}
								className={cn(
									'rounded-md px-2 py-1 text-xs font-semibold tabular-nums transition-colors',
									lbWindow === w
										? 'bg-foreground text-background'
										: 'text-muted-foreground hover:text-foreground'
								)}
							>
								{w}
							</button>
						))}
					</div>
					<div className="mx-3 flex items-center gap-2.5 rounded-xl bg-secondary/40 px-3 py-2.5">
						<GhostAvatar size={32} />
						<span className="flex-1 text-xs text-muted-foreground">Your rank</span>
						<span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-xs font-bold text-primary">
							#&nbsp;—
						</span>
					</div>
					<div aria-hidden="true" className="mt-1 opacity-60">
						{[1, 2, 3, 4, 5, 6, 7].map((n) => (
							<GhostRankRow key={n} n={n} />
						))}
					</div>
					<p className="px-4 pt-1 text-center text-xxs leading-relaxed text-muted-foreground">
						No global per-trader PnL exists for alkanes yet — nothing above is a trader. The{' '}
						{lbWindow} ranking fills in when a ranking service ships.
					</p>
				</div>
			)}

			{tab === 'feed' && <RailFeed />}

			{tab === 'alerts' && (
				<div className="min-h-0 flex-1 overflow-y-auto pb-2">
					<div className="flex items-center gap-3 border-b border-border/40 px-3 py-2 text-xs text-muted-foreground">
						<span>Traders</span>
						<span>Filters</span>
						<span className="flex-1" />
						<span className="text-xxs">coming with follows</span>
					</div>
					<PinnedExplainer text="Alerts are the feed filtered to traders you follow. Follows for wallet-rooted accounts haven't shipped on Twetch's side yet — the rows below are layout only." />
					<div aria-hidden="true" className="opacity-60">
						<GhostEventRow kind="buy" />
						<GhostEventRow kind="thesis" />
						<GhostEventRow kind="sell" />
						<GhostEventRow kind="buy" />
					</div>
				</div>
			)}
		</aside>
	)
}
